import { useQuery, useMutation } from "@tanstack/react-query";
import AppointmentsService, {
  CreateAppointmentPayload,
} from "@/services/appointments.service";
import {
  GetAppointmentsParams,
  Appointment,
} from "@/domain/appointment.interface";

export const useAppointments = ({
  participantId,
  pageNumber = 0,
  pageSize = 5,
  sortField = "startDate",
  sortOrder = "asc",
  filter = "all",
}: GetAppointmentsParams) => {
  const {
    data: appointmentsData,
    isLoading: isLoadingAppointments,
    isFetching: isFetchingAppointments,
    refetch: refetchAppointments,
    error: appointmentsError,
  } = useQuery({
    queryKey: [
      "participant-appointments",
      participantId,
      pageNumber,
      pageSize,
      sortField,
      sortOrder,
      filter,
    ],
    queryFn: () =>
      AppointmentsService.getParticipantAppointments({
        participantId,
        pageNumber,
        pageSize,
        sortField,
        sortOrder,
        filter,
      }),
    enabled: !!participantId, // Only run query if participantId is provided
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  return {
    appointments: appointmentsData?.appointments || [],
    totalCount: appointmentsData?.totalCount || 0,
    isLoadingAppointments,
    isFetchingAppointments,
    refetchAppointments,
    appointmentsError,
  };
};

export const useCreateAppointment = () => {
  return useMutation<
    Appointment,
    Error,
    { enrollmentId: string; userId: string; payload: CreateAppointmentPayload }
  >({
    mutationFn: ({ enrollmentId, userId, payload }) =>
      AppointmentsService.createAppointment({ enrollmentId, userId, payload }),
    retry: 1,
  });
};

export const useUpdateAppointment = () => {
  return useMutation<
    Appointment,
    Error,
    {
      enrollmentId: string;
      appointmentId: string;
      userId: string;
      payload: Partial<CreateAppointmentPayload>;
    }
  >({
    mutationFn: ({ enrollmentId, appointmentId, userId, payload }) =>
      AppointmentsService.updateAppointment({
        enrollmentId,
        appointmentId,
        userId,
        payload,
      }),
    retry: 1,
  });
};
